import React, { useEffect, useState } from 'react';
import { CheckCircle2, MessageSquare, Award, FileEdit, TrendingUp, ClipboardCheck } from 'lucide-react';
import { useDashTheme } from '../../DashboardV2/hooks/useDashTheme';
import { SectionCard, Button } from '../../DashboardV2/components/primitives';
import { getActivityFeed } from '../../../services/goalsPerformanceApi';

const ICONS = {
  goal_submitted: FileEdit,
  goal_approved: CheckCircle2,
  goal_reviewed: ClipboardCheck,
  goal_progress: TrendingUp,
  review_completed: ClipboardCheck,
  badge_earned: Award,
  comment: MessageSquare
};

// Named accent family per event type — resolved from tokens at render.
const ICON_ACCENT = {
  goal_submitted: 'indigo',
  goal_approved: 'green',
  goal_reviewed: 'coolGray',
  goal_progress: 'blue',
  review_completed: 'yellow',
  badge_earned: 'red',
  comment: 'purple'
};

const relativeTime = (ts) => {
  const now = Date.now();
  const then = new Date(ts).getTime();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const RecentActivityCard = ({ canToggleTeam = false }) => {
  const t = useDashTheme();

  const [scope, setScope] = useState('self');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getActivityFeed({ limit: 10, scope })
      .then(res => {
        if (cancelled) return;
        setEvents(res?.data?.events || []);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load activity');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scope]);

  return (
    <SectionCard
      title="Recent Activity"
      style={{ height: '100%' }}
      actions={canToggleTeam ? (
        <>
          <Button size="sm" variant="ghost" active={scope === 'self'} onClick={() => setScope('self')}>Self</Button>
          <Button size="sm" variant="ghost" active={scope === 'team'} onClick={() => setScope('team')}>Team</Button>
        </>
      ) : null}
    >
      <div className="overflow-y-auto pr-1 -mr-1" style={{ maxHeight: 260 }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: t.s[3] }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="animate-pulse" style={{ height: 44, borderRadius: t.r.md, background: t.c.surfaceMuted }} />
            ))}
          </div>
        ) : error ? (
          <div style={{ fontSize: t.font.size.base, color: t.c.textSecondary }}>{error}</div>
        ) : events.length === 0 ? (
          <div style={{ fontSize: t.font.size.base, color: t.c.textSecondary, padding: `${t.s[6]}px 0`, textAlign: 'center' }}>
            No recent activity yet.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: t.s[3] }}>
            {events.map((ev, i) => {
              const Icon = ICONS[ev.type] || FileEdit;
              const pair = t.accentPairs[ICON_ACCENT[ev.type] || 'indigo'];
              return (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: t.s[3] }}>
                  <span style={{
                    width: 28, height: 28, borderRadius: 99, flexShrink: 0,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: pair.bg, color: pair.fg,
                  }}>
                    <Icon size={14} />
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: t.font.size.base, fontWeight: t.font.weight.medium, color: t.c.text }}>{ev.title}</div>
                    {ev.subtitle && (
                      <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, marginTop: 1 }}>{ev.subtitle}</div>
                    )}
                    <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, marginTop: 1 }}>{relativeTime(ev.timestamp)}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </SectionCard>
  );
};

export default RecentActivityCard;
